import type { Option } from "@sorcery/db";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { BiSearch } from "react-icons/bi";
import { cn } from "../../../lib/utils";
import { Input } from "../../atoms/Input";

import { Command, CommandEmpty, CommandItem, CommandList } from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";

export const Combobox = ({
  options,
  onSelect,
  placeholder,
}: {
  options: Option[];
  onSelect: (v: Option) => void;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option>();
  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((o) => {
          return o.label
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""));
        });

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger
        asChild
        onClick={(e) => {
          // force open whenever interacting with the input
          setOpen(true);
          // stop propagation to stop the default trigger behavior toggling the input closed as it expects
          // the trigger to behave like a button, but we have opted for an input
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className="flex items-center [&[data-state=open]>div>svg]:rotate-180 relative">
          <div className="w-full -mb-2">
            <Input
              value={query}
              onChange={(v) => {
                setQuery(v);
                setSelected(undefined);
                // force open whenever interacting with the input
                setOpen(true);
              }}
              LeftComponent={selected ? Check : BiSearch}
              placeholder={placeholder ?? "Start typing..."}
            />
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown className="h-4 w-4 transition-all" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command>
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            {filteredOptions.map((o) => (
              <CommandItem
                key={o.label}
                value={o.value}
                onSelect={() => {
                  setSelected(o);
                  setQuery(o.label);
                  onSelect(o);
                  setOpen(false);
                }}
                className={cn(selected?.value === o.value && "bg-slate-100 text-slate-900")}
              >
                {o.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
